import { Vector3 } from "three";
import { IMeasurementPanelPosition } from "./types";

/** Minimal viewer surface needed by the panel Tick/X/Reset commands. */
export interface IPanelPositionViewer {
  getPanelPosition(id: number): Vector3 | undefined;
  setPanelPosition(id: number, position: Vector3): void;
  resetPanelPosition(id: number): void;
}

export type TSavePanelPosition = (
  id: number,
  position: IMeasurementPanelPosition
) => Promise<{ updateAt: string }>;

export interface IConfirmPanelPositionResult {
  success: boolean;
  updateAt?: string;
}

/**
 * VR-1 / VR-2 (QU3DW-1367): Tick becomes active and the Reset icon appears
 * only once the panel position has actually changed from the value it had
 * when "Edit (Measurement Result)" was entered.
 */
export function hasPanelPositionChanged(
  current: IMeasurementPanelPosition | undefined,
  original: IMeasurementPanelPosition | undefined
): boolean {
  if (!current || !original) return false;
  return (
    current.x !== original.x ||
    current.y !== original.y ||
    current.z !== original.z
  );
}

/**
 * Reset icon (AC-8): move the panel back to the measurement's first point
 * (viewer.resetPanelPosition). Does NOT save to the server — BR-2/A-9: only
 * Tick persists a change, Reset only changes the in-viewer position so that
 * clicking X afterwards can still restore the pre-Edit position (AC-9).
 */
export function resetMeasurementResultPosition(
  viewer: IPanelPositionViewer,
  id: number
): void {
  viewer.resetPanelPosition(id);
}

/**
 * Button X (AC-6, AC-9): always restore the position the panel had before
 * "Edit (Measurement Result)" was entered — regardless of whether Reset was
 * clicked in between (BR-3).
 */
export function cancelMeasurementResultPosition(
  viewer: IPanelPositionViewer,
  id: number,
  previousPosition: IMeasurementPanelPosition
): void {
  viewer.setPanelPosition(
    id,
    new Vector3(previousPosition.x, previousPosition.y, previousPosition.z)
  );
}

/**
 * Button Tick (AC-5, AC-10, AC-11, AC-12, AC-14): read the panel's current
 * position from the viewer and save it immediately (BR-2). On failure, roll
 * the viewer back to the pre-Edit position (BR-11) so the user does not end
 * up with an unsaved, silently-diverged panel.
 *
 * The caller is responsible for disabling Tick/X and the drag mode while this
 * promise is pending (AC-10) — this function only handles the save + rollback
 * logic, not UI state.
 */
export async function confirmMeasurementResultPosition(
  viewer: IPanelPositionViewer,
  id: number,
  previousPosition: IMeasurementPanelPosition,
  save: TSavePanelPosition
): Promise<IConfirmPanelPositionResult> {
  const current = viewer.getPanelPosition(id);
  if (!current) return { success: false };

  const position: IMeasurementPanelPosition = {
    x: current.x,
    y: current.y,
    z: current.z,
  };

  try {
    const { updateAt } = await save(id, position);
    return { success: true, updateAt };
  } catch (err) {
    cancelMeasurementResultPosition(viewer, id, previousPosition);
    return { success: false };
  }
}
